import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@core/services/config.service';
import { faro } from '@grafana/faro-web-sdk';
import { TranslateLoader } from '@ngx-translate/core';
import { TranslationResponse } from '@shared/models/translation-response';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import enTranslations from '../../../assets/i18n/en-GB.json';

@Injectable({
  providedIn: 'root',
})
export class CustomTranslateLoader implements TranslateLoader {
  private api_url: string;
  private cache = new Map<string, { [key: string]: string }>();

  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService,
  ) {
    this.api_url = this.configService.get('ANSWER_SERVICE_API_URL');
  }

  getTranslation(
    lang: string,
    keys: string[] = [],
    fallbackLanguage?: string,
  ): Observable<any> {
    const fallbackKey = fallbackLanguage ?? '';

    const cachedTranslations: { [key: string]: string } = {};
    const uncachedKeys: string[] = [];

    keys.forEach((key) => {
      const cacheKey = `${lang}:${fallbackKey}:${key}`;
      const cached = this.cache.get(cacheKey);

      if (cached) {
        Object.assign(cachedTranslations, cached);
      } else {
        uncachedKeys.push(key);
      }
    });

    if (uncachedKeys.length === 0) {
      return of({ ...enTranslations, ...cachedTranslations });
    }

    const requests = uncachedKeys.map((key) => {
      const url = fallbackLanguage
        ? `${this.api_url}/api/translation/${lang}/${key}?fallbacklanguage=${fallbackLanguage}`
        : `${this.api_url}/api/translation/${lang}/${key}`;

      return this.httpClient
        .get<TranslationResponse>(url, {
          observe: 'response',
        })
        .pipe(
          map((response) => {
            const translations: TranslationResponse | null = response.body;
            const apiTranslationMap: { [key: string]: string } = {};
            translations?.forEach((translation) => {
              apiTranslationMap[translation.key] = translation.text;
            });

            const cacheKey = `${lang}:${fallbackKey}:${key}`;
            this.cache.set(cacheKey, apiTranslationMap);

            return apiTranslationMap;
          }),
          // Fallback in case the API returns no translations or there's an error
          catchError((error) => {
            if (error instanceof Error) {
              faro.api.pushError(error, {
                context: {
                  source: 'CustomTranslateLoader',
                  lang,
                  key,
                  fallbackLanguage: fallbackKey,
                },
              });
            }
            return of(enTranslations);
          }),
        );
    });

    // Use forkJoin to run all requests in parallel
    return forkJoin(requests).pipe(
      // Merge all results to one single object
      map((apiTranslationsArray) => {
        const mergedTranslations = {};
        apiTranslationsArray.forEach((apiTranslations) => {
          Object.assign(mergedTranslations, apiTranslations);
        });
        // Merge: fallback + cached + fresh API results
        return { ...enTranslations, ...cachedTranslations, ...mergedTranslations };
      }),
    );
  }
}
